import clsx from 'clsx'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import StatusIcon from '../../../../UI/StatusIcon'
import FilePreview from '../../../../UI/FilePreview'

import http from '../../../../shared/http'
import { DOCUMENT_TYPES, TApi } from '../../../../shared/const'
import { getFormattedDate } from '../../../../shared/helpers'
import { askReason } from '../../../../shared/confirmDialog'
import { verkleinereBild } from '../../../../shared/shrinkImage'

import styles from './documents.module.scss'

/*
 * Die persönlichen Nachweise einer Schüler:in, im Profilfenster der Lehrkraft
 * (Anforderung #94).
 *
 * Vorher stand in der Ansicht „Schüler:innen" nur, **dass** eine
 * Einverständniserklärung fehlt. Wer sie ansehen wollte, musste sie in der
 * Ansicht „Dokumente" zwischen allen Unterlagen aller betreuten Companies
 * heraussuchen; hochladen konnte sie ohnehin nur die Schüler:in selbst — und
 * genau daran scheitern laut den Landesbetreuer:innen vor allem Junior Basic
 * Companies.
 *
 * Hier steht beides beieinander: der Stand je Nachweis, die hochgeladene Datei
 * zum Ansehen (ein Klick, keine Suche) und — wo die Verwaltung es für diese
 * Unternehmensform freigegeben hat — ein Uploadfeld.
 *
 * Ob hochgeladen werden darf, sagt der Server mit (`mayUpload`) — dieselbe
 * Regel, die er beim Upload selbst noch einmal anlegt. Die Oberfläche fragt sie
 * nicht ein zweites Mal aus den Options nach: Zwei Rechnungen liefen früher
 * oder später auseinander, und diese hier wäre die falsche.
 */

const ACCEPT = {
	[DOCUMENT_TYPES.PARENTAL_CONFIRMATION]: 'application/pdf,image/jpeg,image/png,image/gif'
}

const UPLOAD_URL = {
	[DOCUMENT_TYPES.PARENTAL_CONFIRMATION]: TApi.TEACHER_STUDENT_PARENTAL_CONFIRMATION
}

const FIELD_NAME = {
	[DOCUMENT_TYPES.PARENTAL_CONFIRMATION]: 'parentalConfirmation'
}

// Die Nachweisart, wie sie in der Adresse der beiden Handlungen steht.
const SLUG = {
	[DOCUMENT_TYPES.PARENTAL_CONFIRMATION]: 'parental-confirmation'
}

// 10 MB — dieselbe Grenze, die der Server prüft (utils/helpers, joiFileType).
const MAX_BYTES = 10 * 1024 * 1024

/*
 * Vier Stände je Nachweis, dieselben wie in der Liste dahinter: geprüft,
 * abgegeben, beanstandet, offen. Das Zeichen kommt aus UI/StatusIcon — dieselben
 * Strich-SVGs wie überall sonst, in der Farbe des Textes daneben.
 */
const standOf = ({ document, reviewed, rejected }) => {
	if (document && rejected) return { key: 'rejected', icon: 'rejected', label: 'Rejected', tone: 'rejected' }
	if (document && reviewed) return { key: 'checked', icon: 'check', label: 'Checked', tone: 'checked' }
	if (document) return { key: 'submitted', icon: 'check', label: 'Submitted', tone: 'submitted' }

	return { key: 'open', icon: 'warning', label: 'Open', tone: 'open' }
}

function StudentDocuments(props) {
	const { t } = useTranslation()
	const { student } = props

	const [loading, setLoading] = useState(true)
	const [state, setState] = useState({
		mayUpload: false,
		status: {},
		locked: {},
		documents: []
	})
	const [busy, setBusy] = useState(null)
	const [previewIndex, setPreviewIndex] = useState(-1)

	const studentId = student?.id

	const fetchDocuments = useCallback(() => {
		if (!studentId) return

		setLoading(true)
		http.get(TApi.TEACHER_STUDENT_DOCUMENTS(studentId))
			.then(({ data }) => {
				setPreviewIndex(-1)
				setState({
					mayUpload: Boolean(data.mayUpload),
					/*
					 * Der Stand kommt mit den Nachweisen und nicht aus der Zeile
					 * der Liste: Die kennt ihn vom Öffnen des Fensters. Nach einem
					 * Upload stand dort sonst weiter „Abgewiesen", obwohl die
					 * Beanstandung damit erledigt ist.
					 */
					status: data.status || {},
					/*
					 * Was nach der Aktivierung festgeschrieben ist — je
					 * Nachweisart. Die Regel steht am Server und wird von dort
					 * geholt; sie hier ein zweites Mal zu rechnen hieße, zwei
					 * Antworten auf dieselbe Frage zu haben.
					 */
					locked: data.locked || {},
					documents: data.documents || []
				})
			})
			.finally(() => setLoading(false))
	}, [studentId])

	useEffect(() => {
		fetchDocuments()
	}, [fetchDocuments])

	const documentOf = type => state.documents.find(entry => entry.type === type) || null

	/*
	 * Welche Nachweise überhaupt gelten: die Einverständniserklärung. Bis
	 * 31.10.2026 kam der Kursnachweis dazu, wo die Verwaltung ihn verlangte
	 * (`courseCertificateRequirement`). Er ist ausgebaut; ein schon
	 * hochgeladener steht weiter in der Ansicht „Dokumente".
	 */
	const rows = useMemo(
		() => [
			{
				type: DOCUMENT_TYPES.PARENTAL_CONFIRMATION,
				title: t('Consent of parent or guardian'),
				document: documentOf(DOCUMENT_TYPES.PARENTAL_CONFIRMATION),
				reviewed: Boolean(state.status.reviewedParentalConfirmation),
				rejected: Boolean(state.status.rejectedParentalConfirmation),
				locked: Boolean(state.locked[DOCUMENT_TYPES.PARENTAL_CONFIRMATION])
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[state.documents, state.status, state.locked, t]
	)

	// Geblättert wird durch die Nachweise, die auch dastehen — und nur durch die,
	// zu denen es eine Datei gibt.
	const previewItems = useMemo(
		() =>
			rows
				.filter(row => row.document)
				.map(row => ({
					name: row.document.originalName,
					url: TApi.TEACHER_DOWNLOAD_DOCUMENT(row.document.name),
					file: row.document.name,
					meta: [row.title, getFormattedDate(row.document.createdAt)].filter(Boolean).join(' · ')
				})),
		[rows]
	)

	const download = file => {
		http.get(TApi.TEACHER_DOWNLOAD_DOCUMENT(file), { responseType: 'blob' }).then(({ data }) => {
			const url = window.URL.createObjectURL(new Blob([data]))
			const link = window.document.createElement('a')
			link.href = url
			link.setAttribute('download', file)
			window.document.body.appendChild(link)
			link.click()
			link.parentNode.removeChild(link)
			window.URL.revokeObjectURL(url)
		})
	}

	const handleUpload = async (type, event) => {
		const gewaehlt = event.target.files && event.target.files[0]
		// Das Feld wird geleert, damit dieselbe Datei nach einem Fehlversuch noch
		// einmal ausgewählt werden kann — sonst löst der Browser kein Ereignis aus.
		event.target.value = ''
		if (!gewaehlt) return

		// Fotos gehen verkleinert hoch (shared/shrinkImage); gemessen wird danach —
		// ein 12-MB-Handyfoto ist dann keines mehr.
		const file = await verkleinereBild(gewaehlt)

		if (file.size > MAX_BYTES) {
			toast.error(t('The file is too large. Maximum 10 MB.'))
			return
		}

		const form = new FormData()
		form.append(FIELD_NAME[type], file)

		setBusy(type)
		http.put(UPLOAD_URL[type](studentId), form)
			.then(() => {
				toast.success(t('Document uploaded'))
				fetchDocuments()
				// Die Liste dahinter zeigt denselben Stand — sonst stünde dort
				// weiter „Offen", bis jemand neu lädt.
				props.onChange()
			})
			.finally(() => setBusy(null))
	}

	/*
	 * Beanstanden und Entfernen.
	 *
	 * Beide verlangen eine Begründung — sie geht an die Schüler:in und in den
	 * Verlauf der Junior Company. Eine Datei, die kommentarlos verschwindet,
	 * hinterlässt jemanden, der nicht weiß, was jetzt zu tun ist.
	 *
	 * Der Unterschied in einem Satz: Beanstanden lässt die Datei liegen (die
	 * Erklärung ist die richtige, aber unvollständig), Entfernen löscht sie
	 * (dort liegt etwas, das nicht liegen bleiben darf).
	 */
	const handleObjection = async row => {
		const reason = await askReason({
			title: t('Object to this document?'),
			text: t('The student is informed and can upload a replacement. The file stays and can still be viewed.'),
			label: t('Reason'),
			placeholder: t('e.g. signature is missing'),
			confirmLabel: t('Object to document')
		})
		if (!reason) return

		setBusy(row.type)
		http.post(TApi.TEACHER_STUDENT_DOCUMENT_OBJECTION(studentId, SLUG[row.type]), { reason })
			.then(() => {
				toast.success(t('Document objected to'))
				fetchDocuments()
				props.onChange()
			})
			.finally(() => setBusy(null))
	}

	const handleRemoval = async row => {
		const reason = await askReason({
			title: t('Delete this document?'),
			text: t('The file is deleted for good. The student is informed and has to upload a new one.'),
			label: t('Reason'),
			placeholder: t('e.g. wrong file uploaded'),
			confirmLabel: t('Delete'),
			tone: 'danger'
		})
		if (!reason) return

		setBusy(row.type)
		http.post(TApi.TEACHER_STUDENT_DOCUMENT_REMOVAL(studentId, SLUG[row.type]), { reason })
			.then(() => {
				toast.success(t('Document deleted'))
				fetchDocuments()
				props.onChange()
			})
			.finally(() => setBusy(null))
	}

	if (loading) return <Loading className={styles.loading} />

	return (
		<div className={styles.root}>
			<p className={styles.intro}>
				{state.mayUpload
					? t(
							'You can upload the required documents on behalf of this student. The document stays assigned to the student.'
					  )
					: t('Uploading on behalf of students is not enabled for this company type.')}
			</p>

			<ul className={styles.list}>
				{rows.map(row => {
					const stand = standOf(row)
					const busyHere = busy === row.type

					return (
						<li key={row.type} className={styles.item}>
							<div className={styles.head}>
								<span className={clsx(styles.status, styles[stand.tone])}>
									<StatusIcon name={stand.icon} size={18} />
									{t(stand.label)}
								</span>
								<h4 className={styles.title}>{row.title}</h4>
							</div>

							{row.document ? (
								<div className={styles.file}>
									<button
										type="button"
										className={styles.fileName}
										title={t('Preview')}
										onClick={() =>
											setPreviewIndex(
												previewItems.findIndex(item => item.file === row.document.name)
											)
										}
									>
										{row.document.originalName}
									</button>
									<span className={styles.fileDate}>{getFormattedDate(row.document.createdAt)}</span>
									<Btn
										theme="download"
										icon="download"
										iconSize={{ w: 20, h: 20 }}
										onClick={() => download(row.document.name)}
									/>
								</div>
							) : (
								<p className={styles.missing}>{t('No document uploaded yet')}</p>
							)}

							{state.mayUpload && (
								<div className={styles.actions}>
									{/* Hochladen und Ersetzen nur, solange der Nachweis nicht
									    festgeschrieben ist: Auch eine Ersatzdatei nimmt die
									    Voraussetzung mit, denn die neue ist ungeprüft. */}
									{!row.locked && (
										<label className={clsx(styles.upload, busyHere && styles.uploadBusy)}>
											<input
												type="file"
												accept={ACCEPT[row.type]}
												disabled={busyHere}
												onChange={event => handleUpload(row.type, event)}
											/>
											<span>
												{busyHere
													? t('Uploading…')
													: row.document
													? t('Replace document')
													: t('Upload document')}
											</span>
										</label>
									)}

									{/* Beanstanden und Entfernen gibt es nur, wo etwas
									    liegt — sonst stünden zwei Knöpfe da, die auf
									    nichts zeigen. Und nur, solange der Nachweis
									    nicht festgeschrieben ist: Nach der Aktivierung
									    trägt ein freigegebener Nachweis eine
									    Voraussetzung, die niemand mehr nachprüft. */}
									{row.document && !row.locked && (
										<>
											<button
												type="button"
												className={styles.secondary}
												disabled={busyHere}
												onClick={() => handleObjection(row)}
											>
												{t('Object to document')}
											</button>
											<button
												type="button"
												className={clsx(styles.secondary, styles.danger)}
												disabled={busyHere}
												onClick={() => handleRemoval(row)}
											>
												{t('Delete')}
											</button>
										</>
									)}

									{row.locked && (
										<p className={styles.locked}>
											{t(
												'This company is activated. This document is a requirement for activation and can no longer be replaced, objected to or deleted.'
											)}
										</p>
									)}
								</div>
							)}
						</li>
					)
				})}
			</ul>

			{previewIndex >= 0 && previewItems[previewIndex] && (
				<FilePreview
					items={previewItems}
					index={previewIndex}
					onIndexChange={setPreviewIndex}
					onClose={() => setPreviewIndex(-1)}
					onDownload={item => download(item.file)}
				/>
			)}
		</div>
	)
}

StudentDocuments.propTypes = {
	/** Die Schüler:in, wie die Liste sie mitgegeben hat. */
	student: PropTypes.object,
	/** Wird nach einem Upload gerufen, damit die Liste dahinter nachzieht. */
	onChange: PropTypes.func.isRequired
}

export default StudentDocuments
